/**
 * liveBroadcasterPresence — a tiny cross-tab presence ledger for the community
 * LIVE broadcaster, backed by `localStorage`.
 *
 * Web-specific problem (no Flutter analog — one live screen at a time on mobile):
 * a web session is kept alive across SPA navigation and multiple tabs, so a user
 * who is already broadcasting a LIVE session in one tab could open the community
 * wall in another tab and start a SECOND session from its go-live composer. The
 * note-2 guard (task-prompt lines 9-11) forbids exactly that — "it should not be
 * possible to start a new LIVE session while already broadcasting one" — so the
 * community composer disables its go-live affordance whenever this ledger reports
 * the current user is broadcasting.
 *
 * `localStorage` is shared SYNCHRONOUSLY across all same-origin tabs, so the
 * "am I already broadcasting?" check is a pure local read — no per-check Firestore
 * query and no extra backend read. This is a CLIENT-SIDE UX guard, not a security
 * gate (no server-side single-live enforcement is requested), so a non-secure
 * cross-tab signal mirroring the sibling `liveAudiencePresence` util is the right
 * shape — NOT a `SECURE_STORAGE_KEYS` case.
 *
 * Keyed by `userId` so a user switch on the same browser cannot inherit a stale
 * flag. This is a plain presentation utility — no React hooks
 * (`conventions.md` § New responsibility → new owner file); the reactive read
 * lives in the sibling `useIsBroadcastingLive` hook.
 */
import { appLogger } from '@/domain/services/analytics/appLogger';

/** The single `localStorage` key holding the `{ [userId]: liveMessageId[] }` map. */
const STORAGE_KEY = 'expause.live.broadcasterPresence';

/** `{ [userId]: string[] }` — the live-message ids each user is currently broadcasting. */
type PresenceMap = Record<string, string[]>;

/** SSR / private-mode defensive: `localStorage` is absent outside the browser. */
function getStorage(): Storage | null {
  try {
    if (typeof localStorage === 'undefined') {
      return null;
    }
    return localStorage;
  } catch {
    // Some browsers throw on `localStorage` access under strict privacy settings.
    return null;
  }
}

/** Read + parse the map, tolerating an absent / corrupt value (→ empty map). */
function readMap(storage: Storage): PresenceMap {
  const raw = storage.getItem(STORAGE_KEY);
  if (raw === null) {
    return {};
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (parsed === null || typeof parsed !== 'object') {
      return {};
    }
    return parsed as PresenceMap;
  } catch {
    // Corrupt value → treat as "not broadcasting" (fail-open go-live over a false block).
    return {};
  }
}

/** Serialize the map back, logging (never throwing) on a write failure (quota, etc.). */
function writeMap(storage: Storage, map: PresenceMap): void {
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch (error) {
    appLogger.error(
      'failed to persist live broadcaster presence',
      'liveBroadcasterPresence.writeMap',
      { error: error instanceof Error ? error : new Error(String(error)) },
    );
  }
}

/**
 * True when THIS user currently has ANY live session recorded (in any same-origin
 * tab). Unlike the audience util, the broadcaster guard is not scoped to a single
 * session id — a broadcaster may only ever run one at a time, so any non-empty
 * entry means "already broadcasting". A false / corrupt / absent value reads as
 * "not broadcasting" so a legitimate go-live is never wrongly blocked.
 */
export function isBroadcastingLive(userId: string): boolean {
  const storage = getStorage();
  if (storage === null) {
    return false;
  }
  const ids = readMap(storage)[userId];
  return Array.isArray(ids) && ids.length > 0;
}

/**
 * Record that this user is now broadcasting `liveStreamingMessageId` in the
 * current tab. Idempotent — a second call for an already-present id is a no-op.
 */
export function markBroadcastingLive(
  userId: string,
  liveStreamingMessageId: string,
): void {
  const storage = getStorage();
  if (storage === null) {
    return;
  }
  const map = readMap(storage);
  const ids = Array.isArray(map[userId]) ? map[userId] : [];
  if (ids.includes(liveStreamingMessageId)) {
    return;
  }
  map[userId] = [...ids, liveStreamingMessageId];
  writeMap(storage, map);
}

/**
 * Release this user's presence for `liveStreamingMessageId` (on session end), so a
 * later go-live is allowed. Drops the user's entry entirely once empty to keep the
 * map from accreting stale keys.
 */
export function clearBroadcastingLive(
  userId: string,
  liveStreamingMessageId: string,
): void {
  const storage = getStorage();
  if (storage === null) {
    return;
  }
  const map = readMap(storage);
  const ids = map[userId];
  if (!Array.isArray(ids) || !ids.includes(liveStreamingMessageId)) {
    return;
  }
  const remaining = ids.filter((id) => id !== liveStreamingMessageId);
  if (remaining.length === 0) {
    delete map[userId];
  } else {
    map[userId] = remaining;
  }
  writeMap(storage, map);
}
